import { usePortfolioStore } from '@/stores/portfolioStore';
import { useDividendStore } from '@/stores/dividendStore';
import { useAuthStore } from '@/stores/auth';
import axios from '@/config/axios';
import { isAxiosError } from 'axios';

export interface AssetPriceVariation {
  id: string | number;
  symbol: string;
  name: string;
  average_buy_price: number;
  current_price: number;
  day_variation: number;
  five_day_variation: number;
  month_variation: number;
  year_variation: number;
  year_low: number;
  year_high: number;
  sparkline_data?: string[];
}

/**
 * Service centralisé pour la gestion des portefeuilles et de leurs données associées
 * Ce service coordonne les interactions entre les différents stores
 */
class PortfolioService {
  private static instance: PortfolioService;
  private initialized: boolean = false;

  /**
   * Constructeur privé pour le singleton
   */
  private constructor() {}

  /**
   * Obtenir l'instance unique du service
   */
  public static getInstance(): PortfolioService {
    if (!PortfolioService.instance) {
      PortfolioService.instance = new PortfolioService();
    }
    return PortfolioService.instance;
  }

  /**
   * Initialiser le service et configurer les écouteurs d'événements
   */
  public init(): void {
    if (this.initialized) return;
    
    // Configurer la synchronisation entre les stores
    this.setupStoreSync();
    
    this.initialized = true;
  }

  /**
   * Configurer la synchronisation entre les stores
   */
  private setupStoreSync(): void {
    // Écouter les mises à jour de dividendes et les propager au store du portefeuille
    document.addEventListener('dividend-total-updated', this.handleDividendUpdate.bind(this));
  }

  /**
   * Gérer la mise à jour des dividendes
   */
  private handleDividendUpdate(event: Event): void {
    // Cast de l'événement en CustomEvent pour accéder à detail
    const customEvent = event as CustomEvent;
    const { portfolioId, totalDividends } = customEvent.detail;
    const portfolioStore = usePortfolioStore();
    
    // Mettre à jour le portefeuille courant si disponible (uniquement si la valeur change)
    if (portfolioStore.currentPortfolio && portfolioStore.currentPortfolio.id === portfolioId) {
      const currentVal = Number(portfolioStore.currentPortfolio.total_dividends ?? 0)
      if (currentVal !== Number(totalDividends)) {
        portfolioStore.currentPortfolio.total_dividends = totalDividends;
      }
    }
    
    // Mettre à jour le portefeuille dans la liste des portefeuilles
    const portfolioIndex = portfolioStore.portfolios.findIndex(p => p.id === portfolioId);
    if (portfolioIndex !== -1) {
      const listVal = Number(portfolioStore.portfolios[portfolioIndex].total_dividends ?? 0)
      if (listVal !== Number(totalDividends)) {
        portfolioStore.portfolios[portfolioIndex] = {
          ...portfolioStore.portfolios[portfolioIndex],
          total_dividends: totalDividends
        };
      }
    }
  }

  /**
   * Charger les données complètes d'un portefeuille, y compris les dividendes
   */
  public async loadCompletePortfolioData(portfolioId: number): Promise<void> {
    if (!portfolioId) return;
    
    const portfolioStore = usePortfolioStore();
    const dividendStore = useDividendStore();
    
    try {
      // Charger les données du portefeuille
      await portfolioStore.fetchPortfolioDetails(portfolioId);
      
      // Charger les dividendes pour tous les portefeuilles (utilisateur et partagés)
      // Les permissions sont maintenant gérées côté backend
      await dividendStore.fetchTotalDividends(portfolioId);
      
      // Note: La mise à jour du portefeuille avec les dividendes est gérée par l'écouteur d'événement
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Erreur lors du chargement des données complètes du portefeuille:', error);
      }
    }
  }

  /**
   * Récupérer les variations de cours des actifs d'un portefeuille
   */
  public async fetchAssetPriceVariations(portfolioId: number): Promise<AssetPriceVariation[]> {
    if (!portfolioId) return [];
    
    try {
      // Utiliser axios centralisé qui gère automatiquement l'authentification via les intercepteurs
      const response = await axios.get(`/portfolios/${portfolioId}/asset_price_variations/`);
      return response.data;
    } catch (error: unknown) {
      if (import.meta.env.DEV) {
        console.error('Erreur lors de la récupération des variations de cours:', error);
      }
      
      // Si l'erreur est due à un token expiré (401), essayer de rafraîchir le token
      if (isAxiosError(error) && error.response?.status === 401) {
        try {
          const authStore = useAuthStore();
          await authStore.refreshToken();
          
          // Réessayer après le rafraîchissement du token
          const retryResponse = await axios.get(`/portfolios/${portfolioId}/asset_price_variations/`);
          return retryResponse.data;
        } catch (refreshError) {
          if (import.meta.env.DEV) {
            console.error('Erreur lors du rafraîchissement du token:', refreshError);
          }
          return [];
        }
      }
      
      return [];
    }
  }

  /**
   * Nettoyer les ressources utilisées par le service
   */
  public cleanup(): void {
    document.removeEventListener('dividend-total-updated', this.handleDividendUpdate.bind(this));
    this.initialized = false;
  }
}

// Exporter l'instance singleton du service
export const portfolioService = PortfolioService.getInstance();

// Initialiser automatiquement le service
portfolioService.init();
